/**
 * Qué avisos están esperando respuesta ahora mismo en el inicio del proyecto.
 *
 * Existe por un desajuste de quién sabe qué. Los cuatro avisos que se pintan
 * arriba —invitación a un equipo, traspaso de un sitio, repaso de uno recién
 * recibido, proyectos borrados— se piden CADA UNO por su cuenta al montarse, y
 * se pintan solos o desaparecen sin decírselo a nadie. La pantalla que los aloja
 * no tiene forma de saber si hay alguno: para ella son cuatro componentes que
 * casi siempre devuelven `null`.
 *
 * Y hace falta saberlo, porque la mascota del saludo vive justo debajo. Con algo
 * pendiente encima tiene que levantar la vista y decir qué es; sin nada, seguir
 * a lo suyo. Sin este registro, la única alternativa era subir las cuatro
 * consultas a la pantalla y pasarles los datos hacia abajo — o sea, hacer que la
 * pantalla dependa de cuatro cosas que no le importan, y volver a pedirlas
 * cuando el aviso ya sabía pedirlas solo.
 *
 * ── Por qué esto y no un contexto ───────────────────────────────────────────
 *
 * Es el mismo patrón de `teamInviteSignal`, y por la misma razón: un proveedor
 * tendría que envolver la pantalla entera para comunicar a hermanos que están a
 * dos dedos de distancia, y todo lo que hay dentro se repintaría con cada
 * cambio. Aquí sólo viaja un número por aviso.
 */
import { useSyncExternalStore } from 'react'

/**
 * Los avisos que pueden estar esperando. El orden importa: ver `topNotice`.
 *
 * Los proyectos borrados NO están, y estuvieron: su lista dejó de ser una banda
 * encima de la mascota y pasó a una tarjeta DEBAJO de las formas de empezar. Con
 * la mascota mirando hacia arriba y saltando por algo que está más abajo —y que
 * además tiene treinta días de plazo— la señal apuntaba a un sitio equivocado
 * por una cosa que no urgía. Aquí entra lo que está arriba y espera respuesta.
 */
export type NoticeKind = 'handover' | 'transfer' | 'invite'

export type PendingNotices = Record<NoticeKind, number>

const EMPTY: PendingNotices = { handover: 0, transfer: 0, invite: 0 }

let snapshot: PendingNotices = EMPTY
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot(): PendingNotices {
  return snapshot
}

/**
 * Un aviso dice cuántos tiene pendientes.
 *
 * Se llama al cargar y al resolver, y con cero al desmontarse: un aviso que se
 * va de la pantalla sin retirar su cuenta dejaría a la mascota mirando a un
 * hueco. Si el número no cambia no se avisa a nadie, que es lo que evita un
 * repintado por cada consulta que contesta lo mismo de siempre.
 */
export function reportPending(kind: NoticeKind, count: number): void {
  if (snapshot[kind] === count) return
  snapshot = { ...snapshot, [kind]: count }
  listeners.forEach((listener) => { listener() })
}

export function usePendingNotices(): PendingNotices {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * De qué habla la mascota cuando hay varios.
 *
 * Uno solo, y siempre el mismo: dos frases encadenadas en un globo dejan de ser
 * un comentario y pasan a ser una lista, que es justo lo que ya son los avisos
 * de arriba. El orden es por lo que cuesta ignorarlo —un sitio recién recibido
 * con los cobros caídos pesa más que una invitación que dura una semana—, no por
 * cuál llegó antes.
 */
export function topNotice(notices: PendingNotices): NoticeKind | null {
  const order: NoticeKind[] = ['handover', 'transfer', 'invite']
  return order.find((kind) => notices[kind] > 0) ?? null
}
